/**
 * UUID Helper Functions
 * 
 * Utilities for generating and validating UUIDs in the budget system
 */

/**
 * Generates a UUID v4
 */
export const generateUUID = (): string => {
  // Use crypto.randomUUID if available (modern browsers)
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  
  // Fallback implementation for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

/**
 * Validates if a string is a valid UUID
 */
export const isValidUUID = (uuid: string): boolean => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(uuid);
};

/**
 * Converts a numeric ID to a deterministic UUID
 * Useful for migrating existing numeric IDs
 */
export const numericToUUID = (numericId: number, namespace: string = 'budget'): string => {
  // Create a deterministic UUID based on namespace and numeric ID
  // This ensures the same numeric ID always generates the same UUID
  const hash = `${namespace}-${numericId}`;
  
  // Simple deterministic UUID generation (not cryptographically secure)
  let uuid = '';
  for (let i = 0; i < 32; i++) {
    if (i === 8 || i === 12 || i === 16 || i === 20) {
      uuid += '-';
    }
    const charCode = hash.charCodeAt(i % hash.length);
    const hex = ((charCode + i) % 16).toString(16);
    uuid += hex;
  }
  
  return uuid;
};

/**
 * Ensures a value is a UUID, converting if necessary
 */
export const ensureUUID = (value: string | number | undefined | null): string => {
  if (!value) {
    return generateUUID();
  }
  
  if (typeof value === 'string') {
    // If already a valid UUID, return it
    if (isValidUUID(value)) {
      return value;
    }
    
    // If it's an optimistic ID, generate a new UUID
    if (value.startsWith('optimistic_')) {
      return generateUUID();
    }
    
    // Try to parse as number and convert
    const parsed = parseInt(value);
    if (!isNaN(parsed)) {
      return numericToUUID(parsed);
    }
  }
  
  if (typeof value === 'number') {
    return numericToUUID(value);
  }
  
  // Default to new UUID
  return generateUUID();
};